import { isAxiosError } from 'axios';
import type { HttpClient } from '@shared/http/http-client';
import type { IUserPreferencesRepository } from '@domain/data-contracts/user-preferences-repository.interface';
import type { UiPreferences } from '@domain/models/ui-preferences.interface';
import type { ThemeName } from '@domain/models/theme.enum';
import { DEFAULT_THEME, ALL_THEMES } from '@domain/models/theme.enum';

const ENDPOINT = '/api/me/preferences';
const LOCAL_STORAGE_KEY = 'dcl.ui-preferences';
const DEFAULT_LANGUAGE: UiPreferences['language'] = 'en-GB';

/**
 * HTTP implementation of {@link IUserPreferencesRepository}.
 *
 * Falls back to `localStorage` when the back-end controller has
 * not yet shipped (HTTP 404) so the theme picker remains fully
 * usable in the meantime. Other failure modes (network, 5xx) are
 * propagated so the caller can surface a toast.
 */
export class UserPreferencesRepository implements IUserPreferencesRepository {
  public constructor(private readonly http: HttpClient) {}

  public async get(): Promise<UiPreferences> {
    try {
      const response = await this.http.get<UiPreferences>(ENDPOINT);
      return normalise(response.data);
    } catch (error) {
      if (is404(error)) {
        return readLocal();
      }
      throw error;
    }
  }

  public async updateTheme(theme: ThemeName): Promise<void> {
    writeLocalTheme(theme);
    try {
      await this.http.patch(ENDPOINT, { theme });
    } catch (error) {
      if (is404(error)) {
        return;
      }
      throw error;
    }
  }

  public async updateLanguage(language: UiPreferences['language']): Promise<void> {
    writeLocalLanguage(language);
    try {
      await this.http.patch(ENDPOINT, { language });
    } catch (error) {
      if (is404(error)) {
        return;
      }
      throw error;
    }
  }
}

function is404(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

function normalise(data: Partial<UiPreferences> | null | undefined): UiPreferences {
  const theme = isTheme(data?.theme) ? data!.theme : DEFAULT_THEME;
  const language = data?.language === 'nl-NL' ? 'nl-NL' : DEFAULT_LANGUAGE;
  return { theme, language };
}

function isTheme(value: unknown): value is ThemeName {
  return typeof value === 'string' && (ALL_THEMES as readonly string[]).includes(value);
}

function readLocal(): UiPreferences {
  if (typeof window === 'undefined') {
    return { theme: DEFAULT_THEME, language: DEFAULT_LANGUAGE };
  }
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<UiPreferences>;
      return normalise(parsed);
    }
  } catch {
    // Corrupted JSON — fall through to defaults.
  }
  return { theme: DEFAULT_THEME, language: DEFAULT_LANGUAGE };
}

function writeLocalTheme(theme: ThemeName): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const current = readLocal();
    window.localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({ ...current, theme }),
    );
  } catch {
    // Quota / disabled storage — safe to ignore.
  }
}

function writeLocalLanguage(language: UiPreferences['language']): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const current = readLocal();
    window.localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({ ...current, language }),
    );
  } catch {
    // Quota / disabled storage — safe to ignore.
  }
}
